import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { TaxRule } from "@/hooks/useTaxRules";
import {
  ORIGENS,
  CFOP_OPTIONS,
  CSOSN_OPTIONS,
  CST_PIS_COFINS_OPTIONS,
  NCM_SUGERIDOS,
} from "@/lib/fiscal-constants";

interface TaxRuleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule?: TaxRule | null;
  onSave: (data: Partial<TaxRule>) => Promise<boolean>;
}

export function TaxRuleFormDialog({ open, onOpenChange, rule, onSave }: TaxRuleFormDialogProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    ncm: "",
    cest: "",
    cfop: "5102",
    csosn: "102",
    cst_icms: "",
    icms_aliquota: 0,
    icms_reducao_base: 0,
    cst_pis: "49",
    pis_aliquota: 0,
    cst_cofins: "49",
    cofins_aliquota: 0,
    origem: "0",
  });

  useEffect(() => {
    if (rule) {
      setFormData({
        name: rule.name || "",
        description: rule.description || "",
        ncm: rule.ncm || "",
        cest: rule.cest || "",
        cfop: rule.cfop || "5102",
        csosn: rule.csosn || "102",
        cst_icms: rule.cst_icms || "",
        icms_aliquota: rule.icms_aliquota || 0,
        icms_reducao_base: rule.icms_reducao_base || 0,
        cst_pis: rule.cst_pis || "49",
        pis_aliquota: rule.pis_aliquota || 0,
        cst_cofins: rule.cst_cofins || "49",
        cofins_aliquota: rule.cofins_aliquota || 0,
        origem: rule.origem || "0",
      });
    } else {
      setFormData({
        name: "",
        description: "",
        ncm: "",
        cest: "",
        cfop: "5102",
        csosn: "102",
        cst_icms: "",
        icms_aliquota: 0,
        icms_reducao_base: 0,
        cst_pis: "49",
        pis_aliquota: 0,
        cst_cofins: "49",
        cofins_aliquota: 0,
        origem: "0",
      });
    }
  }, [rule, open]);

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.ncm.trim()) {
      return;
    }

    setSaving(true);
    const success = await onSave(formData);
    setSaving(false);

    if (success) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rule ? "Editar Regra Tributária" : "Nova Regra Tributária"}</DialogTitle>
          <DialogDescription>
            Defina os códigos e alíquotas fiscais para esta regra
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Identificação */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome da Regra *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Alimentos Prontos, Bebidas, Revenda..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição opcional..."
                rows={2}
              />
            </div>
          </div>

          <Separator />

          {/* Códigos Fiscais */}
          <div className="space-y-4">
            <h4 className="font-medium">Códigos Fiscais</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ncm">NCM *</Label>
                <Select
                  value={formData.ncm}
                  onValueChange={(value) => setFormData({ ...formData, ncm: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione ou digite..." />
                  </SelectTrigger>
                  <SelectContent>
                    {NCM_SUGERIDOS.map((ncm) => (
                      <SelectItem key={ncm.value} value={ncm.value}>
                        {ncm.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={formData.ncm}
                  onChange={(e) => setFormData({ ...formData, ncm: e.target.value.replace(/\D/g, "") })}
                  placeholder="Ou digite o NCM..."
                  maxLength={8}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cest">CEST</Label>
                <Input
                  id="cest"
                  value={formData.cest}
                  onChange={(e) => setFormData({ ...formData, cest: e.target.value.replace(/\D/g, "") })}
                  placeholder="Ex: 1705800"
                  maxLength={7}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cfop">CFOP</Label>
                <Select
                  value={formData.cfop}
                  onValueChange={(value) => setFormData({ ...formData, cfop: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CFOP_OPTIONS.map((cfop) => (
                      <SelectItem key={cfop.value} value={cfop.value}>
                        {cfop.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="origem">Origem</Label>
                <Select
                  value={formData.origem}
                  onValueChange={(value) => setFormData({ ...formData, origem: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ORIGENS.map((origem) => (
                      <SelectItem key={origem.value} value={origem.value}>
                        {origem.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Separator />

          {/* ICMS */}
          <div className="space-y-4">
            <h4 className="font-medium">ICMS (Simples Nacional)</h4>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="csosn">CSOSN</Label>
                <Select
                  value={formData.csosn}
                  onValueChange={(value) => setFormData({ ...formData, csosn: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CSOSN_OPTIONS.map((csosn) => (
                      <SelectItem key={csosn.value} value={csosn.value}>
                        {csosn.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="icms_aliquota">Alíquota ICMS (%)</Label>
                <Input
                  id="icms_aliquota"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={formData.icms_aliquota}
                  onChange={(e) => setFormData({ ...formData, icms_aliquota: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="icms_reducao_base">Redução BC (%)</Label>
                <Input
                  id="icms_reducao_base"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={formData.icms_reducao_base}
                  onChange={(e) => setFormData({ ...formData, icms_reducao_base: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* PIS/COFINS */}
          <div className="space-y-4">
            <h4 className="font-medium">PIS / COFINS</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cst_pis">CST PIS</Label>
                <Select
                  value={formData.cst_pis}
                  onValueChange={(value) => setFormData({ ...formData, cst_pis: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CST_PIS_COFINS_OPTIONS.map((cst) => (
                      <SelectItem key={cst.value} value={cst.value}>
                        {cst.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pis_aliquota">Alíquota PIS (%)</Label>
                <Input
                  id="pis_aliquota"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={formData.pis_aliquota}
                  onChange={(e) => setFormData({ ...formData, pis_aliquota: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cst_cofins">CST COFINS</Label>
                <Select
                  value={formData.cst_cofins}
                  onValueChange={(value) => setFormData({ ...formData, cst_cofins: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CST_PIS_COFINS_OPTIONS.map((cst) => (
                      <SelectItem key={cst.value} value={cst.value}>
                        {cst.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cofins_aliquota">Alíquota COFINS (%)</Label>
                <Input
                  id="cofins_aliquota"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={formData.cofins_aliquota}
                  onChange={(e) => setFormData({ ...formData, cofins_aliquota: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || !formData.name.trim() || !formData.ncm.trim()}>
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
